/**
 * Build a v1 snapshot from a station's persisted state.
 *
 * Pure projection: takes already-loaded domain snapshots in, produces the
 * wire-format object the YAML spec defines. Date conversions (epoch-ms →
 * ISO) happen here so the schema can stay strict about its inputs.
 *
 * The optional flags let the UI exclude chargingProfiles or offlineQueue
 * before download — both are toggled to "include" by default per
 * data/snapshot-format.yaml#export.optional_sections.
 */
import type { ChargingProfileSnapshot } from '@/domain/ChargingProfile';
import type { ConfigurationKeySnapshot } from '@/domain/ConfigurationKey';
import type { OfflineSessionSnapshot } from '@/domain/OfflineSession';
import type { StationSnapshot } from '@/domain/Station';
import type { LocalAuthListEntryRow, VariableValueSnapshot } from '@/persistence/ports';
import type {
  SnapshotChargingProfileV1,
  SnapshotConfigurationKeyV1,
  SnapshotEVSEV1,
  SnapshotLocalAuthEntryV2,
  SnapshotOfflineSessionV1,
  SnapshotStationV1,
  SnapshotV1,
  SnapshotVariableValueV2,
} from './snapshotSchema';

export interface BuildSnapshotInput {
  readonly station: StationSnapshot;
  readonly configuration: readonly ConfigurationKeySnapshot[];
  readonly chargingProfiles: readonly ChargingProfileSnapshot[];
  readonly offlineQueue: readonly OfflineSessionSnapshot[];
  /** v2 — OCPP 2.0.1 Device Model rows (empty for a 1.6 station). */
  readonly variableValues: readonly VariableValueSnapshot[];
  /** v2 — Local Authorization List + its version. */
  readonly localAuthList: { readonly version: number; readonly entries: readonly LocalAuthListEntryRow[] };
  readonly exportedBy: string;
  readonly exportedAt?: number;
  readonly include?: {
    readonly chargingProfiles?: boolean;
    readonly offlineQueue?: boolean;
    readonly deviceModel?: boolean;
    readonly localAuthList?: boolean;
  };
}

export function buildSnapshot(input: BuildSnapshotInput): SnapshotV1 {
  const includeProfiles = input.include?.chargingProfiles !== false;
  const includeOffline = input.include?.offlineQueue !== false;
  const includeDeviceModel = input.include?.deviceModel !== false;
  const includeLocalAuth = input.include?.localAuthList !== false;
  const station: SnapshotStationV1 = {
    cpid: input.station.cpid,
    name: input.station.displayName ?? input.station.cpid,
    wsUrl: input.station.wsUrl,
    ocppVersion: input.station.ocppVersion,
    topology: {
      cpMaxPowerW: input.station.topology.cpMaxPowerW,
      evses: input.station.topology.evses.map(toEvseV1),
    },
    configuration: input.configuration.map(toConfigurationKeyV1),
    chargingProfiles: includeProfiles ? input.chargingProfiles.map(toChargingProfileV1) : [],
    offlineQueue: includeOffline ? input.offlineQueue.map(toOfflineSessionV1) : [],
    variableValues: includeDeviceModel ? input.variableValues.map(toVariableValueV2) : [],
    localAuthList: includeLocalAuth
      ? { version: input.localAuthList.version, entries: input.localAuthList.entries.map(toLocalAuthEntryV2) }
      : { version: 0, entries: [] },
  };
  return {
    schemaVersion: 2,
    exportedAt: new Date(input.exportedAt ?? Date.now()).toISOString(),
    exportedBy: input.exportedBy,
    station,
  };
}

/** Stable filename per data/snapshot-format.yaml#export.filename_template. */
export function snapshotFilename(cpid: string, at: number = Date.now()): string {
  const iso = new Date(at).toISOString().replace(/[:.]/g, '-');
  const safeCpid = cpid.replace(/[^A-Za-z0-9_.-]/g, '_');
  return `snapshot-station-${safeCpid}-${iso}.json`;
}

/** Pretty-print with the 2-space indent the spec mandates. */
export function snapshotToJson(snap: SnapshotV1): string {
  return JSON.stringify(snap, null, 2);
}

function toEvseV1(e: StationSnapshot['topology']['evses'][number]): SnapshotEVSEV1 {
  return {
    id: Number(e.id),
    name: e.name,
    maxPowerW: e.maxPowerW,
    connectorIds: e.connectorIds.map((c) => Number(c)),
  };
}

function toConfigurationKeyV1(k: ConfigurationKeySnapshot): SnapshotConfigurationKeyV1 {
  return {
    key: k.key,
    value: k.value,
    readonly: k.readonly,
    type: k.type,
    section: k.section,
  };
}

function toChargingProfileV1(p: ChargingProfileSnapshot): SnapshotChargingProfileV1 {
  return {
    id: Number(p.id),
    connectorId: p.connectorId,
    purpose: p.purpose,
    stackLevel: p.stackLevel,
    kind: p.kind,
    ...(p.recurrencyKind !== undefined ? { recurrencyKind: p.recurrencyKind } : {}),
    ...(p.validFrom !== undefined ? { validFrom: new Date(p.validFrom).toISOString() } : {}),
    ...(p.validTo !== undefined ? { validTo: new Date(p.validTo).toISOString() } : {}),
    ...(p.transactionId !== undefined ? { transactionId: Number(p.transactionId) } : {}),
    schedule: {
      ...(p.schedule.startSchedule !== undefined
        ? { startSchedule: new Date(p.schedule.startSchedule).toISOString() }
        : {}),
      ...(p.schedule.duration !== undefined ? { duration: p.schedule.duration } : {}),
      chargingRateUnit: p.schedule.chargingRateUnit,
      ...(p.schedule.minChargingRate !== undefined
        ? { minChargingRate: p.schedule.minChargingRate }
        : {}),
      chargingSchedulePeriod: p.schedule.chargingSchedulePeriod.map((pp) => ({
        startPeriod: pp.startPeriod,
        limit: pp.limit,
        ...(pp.numberPhases === 1 || pp.numberPhases === 2 || pp.numberPhases === 3
          ? { numberPhases: pp.numberPhases }
          : {}),
      })),
    },
  };
}

function toVariableValueV2(v: VariableValueSnapshot): SnapshotVariableValueV2 {
  return {
    component: v.component,
    variable: v.variable,
    evseId: v.evseId,
    connectorId: v.connectorId,
    attributeType: v.attributeType,
    value: v.value,
    mutability: v.mutability,
    dataType: v.dataType,
    ...(v.unit !== undefined ? { unit: v.unit } : {}),
    section: v.section,
  };
}

function toLocalAuthEntryV2(r: LocalAuthListEntryRow): SnapshotLocalAuthEntryV2 {
  return {
    idTag: r.idTag,
    status: r.status,
    ...(r.expiryDate !== undefined ? { expiryDate: r.expiryDate } : {}),
    ...(r.parentIdTag !== undefined ? { parentIdTag: r.parentIdTag } : {}),
  };
}

function toOfflineSessionV1(s: OfflineSessionSnapshot): SnapshotOfflineSessionV1 {
  return {
    idTag: s.idTag,
    connectorId: Number(s.connectorId),
    startedAt: new Date(s.startedAt).toISOString(),
    duration: s.duration,
    meterStartWh: s.meterStartWh,
    energyDeliveredWh: s.energyDeliveredWh,
    meterValueIntervalSec: s.meterValueIntervalSec,
    ...(s.startSoC !== undefined ? { startSoC: s.startSoC } : {}),
    ...(s.endSoC !== undefined ? { endSoC: s.endSoC } : {}),
    voltage: s.voltage,
    stopReason: s.stopReason,
  };
}
